import moment from 'moment';
import { consoleLogInDev, logRejectionsInDev, client, track } from 'shared';
import {
    DayOfWeekSelections,
    PostBodyType,
    JobOptions,
    RequestableProvider,
    WorkingHoursForDay,
} from './JobRequestTypes';
import { JobTypeIds } from '../../models/JobType';
import { Job } from 'models';

export enum JobType {
    ongoing = 'Ongoing',
    onetime = 'onetime',
}

interface SelectedDay {
    day: string;
    start: moment.Moment;
    end: moment.Moment;
    slots: number;
}

interface JobSubmissionResult {
    providerWasRequested: boolean;
    submissionSucceeded: boolean;
    createdJobId?: number | null;
    job?: Job;
}

export default async function submitJobRequest({
    familyId,
    payMin,
    payMax,
    jobOptions,
    lastName,
    jobType,
    preferredProviders,
    invitedProvider,
    jobTypeId,
    otherJobType,
    acceptedTandemPayAgreement,
    code,
    status,
    jobId,
}: {
    familyId: number;
    payMin: number;
    payMax: number;
    jobOptions: JobOptions;
    lastName: string;
    jobType: JobType;
    preferredProviders: RequestableProvider[];
    invitedProvider: { id: number } | undefined;
    jobTypeId: number;
    otherJobType: string;
    acceptedTandemPayAgreement: boolean | undefined;
    code: string;
    status: string;
    jobId: number;
}): Promise<JobSubmissionResult | PostBodyType> {
    const submissionResult: JobSubmissionResult = {
        submissionSucceeded: true,
        providerWasRequested: false,
    };

    const workingHoursByDay = getWorkingHours(jobOptions);
    let ongoingTimesOfDay = getOngoingTimesOfDay(workingHoursByDay);
    let selectedWeekDays = workingHoursByDay.map((hoursInfo) => hoursInfo.day);
    if ((!selectedWeekDays || selectedWeekDays.length === 0) && jobOptions.start && jobOptions.end_time) {
        const day = jobOptions.start.toDate().toLocaleString('en-us', { weekday: 'long' }) as keyof DayOfWeekSelections;
        selectedWeekDays = [day];
        ongoingTimesOfDay = [
            {
                [day]: {
                    start: jobOptions.start.format('HH:mm:ss'),
                    end: jobOptions.end_time.format('HH:mm:ss'),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    // eslint-disable-next-line security/detect-object-injection
                    slots: jobOptions.daysOfTheWeek[day].slots,
                },
            },
        ];
    }
    let newJobTypeId = undefined;
    if (jobTypeId < 0) {
        const res = await client('api/job-types/', {
            body: { name: otherJobType },
        });

        newJobTypeId = res.id;
    }

    const ongoingRequest = createOngoingRequest(
        selectedWeekDays,
        ongoingTimesOfDay,
        jobOptions,
        payMin,
        payMax,
        familyId,
        acceptedTandemPayAgreement,
        newJobTypeId || jobTypeId,
        jobType,
        status,
    );
    if (status === 'DRAFT') {
        return ongoingRequest;
    }

    const addresses = jobOptions.address;
    for (let i = 0; i < addresses.length; i++) {
        const onGoingToSubmit = { ...ongoingRequest, address: addresses[i] };

        const ongoingResponse = await submitOngoingRequest(onGoingToSubmit, i === 0 ? jobId : 0);

        if (ongoingResponse) {
            sendOliviaText(lastName);
            if (ongoingResponse.id) {
                const ongoingId = ongoingResponse.id;
                submissionResult.createdJobId = ongoingId;
                submissionResult.job = ongoingResponse;

                if (code) {
                    submitPromoCode(code);
                }

                if (invitedProvider) {
                    await createRequestedPairing(ongoingId, invitedProvider.id, jobType, submissionResult);
                }

                await Promise.all(
                    jobOptions.selectedPreferredProviders.map(async (providerUserId: number) => {
                        const provider = preferredProviders.find((provider) => provider.id === providerUserId)!;
                        await createNonrequestedPairing(ongoingId, provider.providerId);
                        await inviteProvider(provider, ongoingResponse);
                    }),
                );
            } else {
                submissionResult.submissionSucceeded = false;
            }
        }
    }

    return submissionResult;
}

function sendOliviaText(lastName: string) {
    let requestObject = {
        phone_number: '+16144030857',
        message: 'The ' + lastName + ' Family made a recurring request.',
    };

    client('send-sms-directly-to-phone-number/', { body: requestObject }).catch(consoleLogInDev);
}

const createNonrequestedPairing = logRejectionsInDev(async (jobId: number, providerId: number) => {
    return createProviderPairing(jobId, providerId, false);
});

const createRequestedPairing = logRejectionsInDev(
    async (jobId: number, providerId: number, jobType: string, submissionResult: JobSubmissionResult) => {
        await createProviderPairing(jobId, providerId, true);

        track(`RT/P@P: Created Requested Pairing from ${jobType} Job Form`);

        submissionResult.providerWasRequested = true;
    },
);

async function createProviderPairing(jobId: number, providerId: number, isRequested: boolean) {
    await client('api/pairings/create_from_job_request/', {
        body: {
            provider_id: providerId,
            ongoing_request_id: jobId,
            requested: isRequested,
        },
    });
}

function getWorkingHours(jobOptions: JobOptions) {
    const selectedDaysOfWeek = Object.keys(jobOptions.daysOfTheWeek) as (keyof DayOfWeekSelections)[];

    // The reason we check for sameTimes here is because the user is able to select the 'same times' option
    // In the UI and also individual times for each day. If they select the same times option, we default to
    // using the start and end_time rather than the individual times for each day.
    const dayOfWeekObjects = selectedDaysOfWeek
        .map((dayName: keyof DayOfWeekSelections) => ({
            day: dayName,
            // eslint-disable-next-line security/detect-object-injection
            start: jobOptions.sameTimes ? jobOptions.start : jobOptions.daysOfTheWeek[dayName].start,
            // eslint-disable-next-line security/detect-object-injection
            end: jobOptions.sameTimes ? jobOptions.end_time : jobOptions.daysOfTheWeek[dayName].end,
            // eslint-disable-next-line security/detect-object-injection
            slots: jobOptions.daysOfTheWeek[dayName].slots,
        }))
        .filter((hoursForDay) => jobOptions.daysOfTheWeek[hoursForDay.day].selected);

    return dayOfWeekObjects;
}

function getOngoingTimesOfDay(workingHoursByDay: SelectedDay[]) {
    return workingHoursByDay.map(timeSpanObjectForDay);
}

function timeSpanObjectForDay(hoursForDay: SelectedDay) {
    return {
        [hoursForDay.day]: {
            start: hoursForDay.start.format('HH:mm:ss'),
            end: hoursForDay.end.format('HH:mm:ss'),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            slots: hoursForDay.slots,
        },
    };
}

export function createOngoingRequest(
    daysOfWeek: string[],
    timesOfDay: WorkingHoursForDay[],
    jobOptions: JobOptions,
    payMin: number,
    payMax: number,
    familyId: number,
    acceptedTandemPayAgreement: boolean | undefined,
    jobTypeId: number,
    jobType: string,
    status: string,
) {
    let start = jobOptions.start ? moment(jobOptions.start, 'ddd MMM DD YYYY HH:mm:ss').format('YYYY-MM-DD') : null;
    let end = jobOptions.end ? moment(jobOptions?.end, 'ddd MMM DD YYYY HH:mm:ss').format('YYYY-MM-DD') : null;

    const payIsRange = payMin !== payMax;
    const payIsLumpSum = false;

    let requestType = 'CHILD_CARE';
    if (jobTypeId === JobTypeIds.PetCare) {
        requestType = 'PET_CARE';
    } else if (jobTypeId > JobTypeIds.PetCare) {
        requestType = 'OTHER';
    }

    let ongoing = {
        family: familyId,
        start_date: jobOptions?.startEstimate !== 'OTHER' ? start : null,
        end_date: jobOptions?.startEstimate !== 'OTHER' ? end : null,
        pay: payIsRange ? null : payMin,
        pay_is_fixed: payIsLumpSum,
        rate_min: payIsRange ? payMin : null,
        rate_max: payIsRange ? payMax : null,
        transportation: jobOptions.transportation,
        multiple_providers: jobOptions.multipleProviders,
        status: status,
        quick_fill: false,
        kids_present: getKidsObject(jobOptions.kids),
        family_comments: jobOptions.comments,
        days_of_week: daysOfWeek,
        preferred_sitters: getPreferredSittersObject(jobOptions.pastSitters),
        times_of_day: timesOfDay,
        start_month: jobOptions.startEstimate === 'OTHER' ? jobOptions.startEstimateMonth : null,
        ongoing: jobType !== 'onetime',
        headline: jobOptions?.headline || null,
        request_type: requestType,
        job_type: jobTypeId,
        accepted_tandem_pay_agreement: acceptedTandemPayAgreement,
        pay_window: jobOptions.paymentSchedule?.payWindow || 'DAILY',
        qualification_requirements: jobOptions.jobQualifications,
        address: jobOptions.address,
        pay_type: jobOptions.pay_type,
        business_job_type: jobOptions.business_job_type,
        pay_range_info: jobOptions.pay_range_info,
        job_details: jobOptions.jobDetails,
        pay_scales: jobOptions.payScales.filter((x: any) => x.description),
        break_required: jobOptions.breakRequired,
        break_length: jobOptions.breakLength,
        slots_available: jobOptions.slotsAvailable,
    };
    return ongoing;
}

function getKidsObject(kids: number[]) {
    return { kids: kids.map((kid) => ({ kid })) };
}

function getPreferredSittersObject(preferredProviders: number[]) {
    return {
        preferred_sitters: preferredProviders.map((preferredProvider: number) => ({
            sitter: preferredProvider,
        })),
    };
}

function submitOngoingRequest(request: object, id: number) {
    return client(`api/ongoing/${id ? id : ''}`, {
        method: id ? 'PATCH' : 'POST',
        body: request,
    }).catch(consoleLogInDev);
}

function inviteProvider(provider: RequestableProvider, job: { id: number }) {
    const invitationMessage = `Hello, ${provider.firstName}! I think you could be a good fit for my job. Please review the details and apply if you're interested. Thanks, I look forward to hearing from you!`;

    return client(`api/invitation/`, {
        body: {
            invited_user: provider.id,
            body: invitationMessage,
            job_request: job.id,
        },
    });
}

async function submitPromoCode(code: string) {
    try {
        let body = { code: code };
        await client(`api/promo-code/apply/`, { body });
    } catch (error) {
        consoleLogInDev(error);
    }
}
